import React from 'react';
import {
  Network,
  Database,
  Settings,
  Server,
  Shield,
  CheckCircle,
  AlertTriangle,
  Code,
  Users,
  FileText,
  Layers,
  GitMerge
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const Integration = () => {
  const integrationSteps = [
    {
      phase: "1. Analyse Préliminaire",
      description: "Évaluation du contexte client",
      steps: [
        "Audit système existant",
        "Identification des interfaces",
        "Analyse des contraintes techniques",
        "Évaluation des risques"
      ],
      deliverables: [
        "Rapport d'audit technique",
        "Matrice de compatibilité",
        "Plan de migration données"
      ]
    },
    {
      phase: "2. Préparation Environnement",
      description: "Setup infrastructure d'intégration",
      steps: [
        "Configuration serveurs dédiés",
        "Mise en place VPN/connexions",
        "Setup monitoring spécifique",
        "Création base de données isolée"
      ],
      deliverables: [
        "Documentation infrastructure",
        "Accès et credentials",
        "Dashboard monitoring"
      ]
    },
    {
      phase: "3. Adaptation Solution",
      description: "Customisation du produit",
      steps: [
        "Configuration core system",
        "Adaptation interfaces client",
        "Paramétrage règles métier",
        "Setup workflows spécifiques"
      ],
      deliverables: [
        "Configuration documentée",
        "Matrices de paramétrage",
        "Documentation adaptations"
      ]
    },
    {
      phase: "4. Tests d'Intégration",
      description: "Validation technique complète",
      steps: [
        "Tests connectivité bout en bout",
        "Validation flux de données",
        "Tests performance système",
        "Vérification sécurité"
      ],
      deliverables: [
        "Rapports de tests",
        "Résultats performance",
        "Validation sécurité"
      ]
    },
    {
      phase: "5. Migration Données",
      description: "Transfert données client",
      steps: [
        "Extraction données sources",
        "Transformation format KG",
        "Chargement et vérification",
        "Validation intégrité"
      ],
      deliverables: [
        "Rapport de migration",
        "Logs de validation",
        "Matrices de mapping"
      ]
    }
  ];

  const technicalChecklist = [
    {
      category: "Sécurité",
      items: [
        "Chiffrement données sensibles",
        "Configuration IAM/RBAC",
        "Audit logs activés",
        "Scan vulnérabilités"
      ]
    },
    {
      category: "Performance",
      items: [
        "Sizing environnement",
        "Configuration cache",
        "Optimisation requêtes",
        "Monitoring temps réel"
      ]
    },
    {
      category: "Disponibilité",
      items: [
        "Setup haute disponibilité",
        "Plan de backup",
        "Procédures reprise",
        "Monitoring santé"
      ]
    }
  ];

  const integrationPatterns = [
    {
      type: "APIs REST",
      description: "Interfaces REST standardisées",
      requirements: [
        "Documentation OpenAPI",
        "Gestion des versions",
        "Sécurisation endpoints",
        "Rate limiting configuré"
      ]
    },
    {
      type: "Message Queue",
      description: "Communication asynchrone",
      requirements: [
        "Setup file messages",
        "Gestion retry/DLQ",
        "Monitoring messages",
        "Alerting configuré"
      ]
    },
    {
      type: "Base de données",
      description: "Intégration données",
      requirements: [
        "Scripts migration",
        "Gestion des locks",
        "Backup/Restore",
        "Monitoring performance"
      ]
    }
  ];

  const validationCriteria = [
    {
      area: "Fonctionnel",
      criteria: [
        "Workflows client validés",
        "Règles métier vérifiées",
        "Rapports générés OK",
        "Interfaces utilisateur OK"
      ]
    },
    {
      area: "Technique",
      criteria: [
        "Performance conforme SLA",
        "Sécurité validée",
        "Monitoring opérationnel",
        "Backup/restore OK"
      ]
    },
    {
      area: "Données",
      criteria: [
        "Migration complète",
        "Intégrité vérifiée",
        "Historique disponible",
        "Archivage configuré"
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            Méthodologie d'Intégration Solutions KG TECHNOLOGIES
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            Guide d'intégration et déploiement
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Phases d'Intégration */}
          <div className="space-y-6">
            {integrationSteps.map((phase, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white font-bold text-lg mr-4">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-blue-600">{phase.phase}</h3>
                    <p className="text-gray-600 mb-4">{phase.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Étapes */}
                      <div className="space-y-2">
                        <h4 className="font-medium flex items-center text-gray-700">
                          <Settings className="w-4 h-4 mr-2" />
                          Étapes
                        </h4>
                        <ul className="space-y-1">
                          {phase.steps.map((step, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Livrables */}
                      <div className="space-y-2">
                        <h4 className="font-medium flex items-center text-gray-700">
                          <FileText className="w-4 h-4 mr-2" />
                          Livrables
                        </h4>
                        <ul className="space-y-1">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Patterns d'Intégration */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Network className="mr-2" />
              Patterns d'Intégration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {integrationPatterns.map((pattern, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-2">{pattern.type}</h4>
                  <p className="text-sm text-gray-600 mb-3">{pattern.description}</p>
                  <ul className="space-y-2">
                    {pattern.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist Technique */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Shield className="mr-2" />
              Checklist Technique
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalChecklist.map((check, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{check.category}</h4>
                  <ul className="space-y-2">
                    {check.items.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Critères de Validation */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <CheckCircle className="mr-2" />
              Critères de Validation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {validationCriteria.map((criteria, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{criteria.area}</h4>
                  <ul className="space-y-2">
                    {criteria.criteria.map((criterion, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                        {criterion}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Points d'Attention */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-700 mb-2">Points d'Attention</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-800">
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Toujours préserver l'intégrité des données
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Tester les procédures de rollback
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Valider les performances en charge
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Maintenir la documentation à jour
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Integration;